import { useState } from "react";

// Sample testimonial data
const testimonials = [
	{
		id: "1",
		name: "Sarah Chen",
		role: "Product Manager",
		company: "TechFlow",
		avatar: null,
		rating: 5,
		content:
			"This platform has completely transformed how we collect and showcase customer feedback. The wall of love feature is a game-changer for our marketing efforts!",
		type: "text",
		tags: ["Product", "Marketing"],
	},
	{
		id: "2",
		name: "Michael Roberts",
		role: "CEO",
		company: "StartupX",
		avatar: null,
		rating: 5,
		content: "Absolutely love it! Our conversion rates increased by 40% after adding the testimonial widget to our landing page.",
		type: "text",
		tags: ["Sales"],
		image: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=400&h=300&fit=crop",
	},
	{
		id: "3",
		name: "Emily Watson",
		role: "Marketing Director",
		company: "GrowthLabs",
		avatar: null,
		rating: 5,
		content: "The best testimonial management tool I've ever used. Clean interface, powerful features, and excellent support team.",
		type: "text",
		tags: ["Product"],
	},
	{
		id: "4",
		name: "David Kim",
		role: "Founder",
		company: "DesignStudio",
		avatar: null,
		rating: 5,
		content:
			"We switched from our old solution and never looked back. The customization options are incredible and the embed codes work flawlessly on any platform.",
		type: "text",
		tags: ["Product", "Design"],
	},
	{
		id: "5",
		name: "Jessica Taylor",
		role: "Head of Growth",
		company: "SaaSify",
		avatar: null,
		rating: 5,
		content: "Finally, a testimonial tool that understands what modern businesses need. The video testimonial feature is phenomenal!",
		type: "video",
		tags: ["Video", "Growth"],
		videoThumbnail: "https://images.unsplash.com/photo-1557804506-669a67965ba0?w=400&h=300&fit=crop",
	},
	{
		id: "6",
		name: "Alex Thompson",
		role: "UX Designer",
		company: "PixelPerfect",
		avatar: null,
		rating: 5,
		content: "The attention to detail in this product is remarkable. Every feature feels thoughtfully designed.",
		type: "text",
		tags: ["Design"],
	},
	{
		id: "7",
		name: "Rachel Green",
		role: "Content Strategist",
		company: "ContentHub",
		avatar: null,
		rating: 5,
		content:
			"Collecting testimonials used to be a nightmare. Now it takes minutes, and the results look professional without any extra work from our team.",
		type: "text",
		tags: ["Marketing", "Content"],
		image: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=400&h=300&fit=crop",
	},
	{
		id: "8",
		name: "James Wilson",
		role: "Sales Director",
		company: "CloseDeal",
		avatar: null,
		rating: 5,
		content: "Our sales team uses the testimonials daily in their pitches. The ROI has been incredible.",
		type: "text",
		tags: ["Sales"],
	},
	{
		id: "9",
		name: "Lisa Anderson",
		role: "VP of Marketing",
		company: "BrandBoost",
		avatar: null,
		rating: 5,
		content:
			"I've tried countless testimonial tools over the years. This one stands out for its simplicity and power. Highly recommend for any business serious about social proof.",
		type: "text",
		tags: ["Marketing"],
	},
	{
		id: "10",
		name: "Chris Martinez",
		role: "Developer",
		company: "CodeCraft",
		avatar: null,
		rating: 5,
		content: "The API is well-documented and the integration was seamless. Great developer experience!",
		type: "text",
		tags: ["Product"],
	},
	{
		id: "11",
		name: "Amanda Foster",
		role: "Customer Success",
		company: "HappyClients",
		avatar: null,
		rating: 5,
		content: "Our customers love leaving feedback through the forms. The video option especially has helped us capture authentic stories.",
		type: "video",
		tags: ["Video", "Support"],
		videoThumbnail: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=300&fit=crop",
	},
	{
		id: "12",
		name: "Nathan Brooks",
		role: "Agency Owner",
		company: "GrowthAgency",
		avatar: null,
		rating: 5,
		content: "We use this for all our clients now. The white-label options make it perfect for agencies.",
		type: "text",
		tags: ["Marketing", "Agency"],
	},
];

const filterTags = ["All", "Product", "Marketing", "Sales", "Video", "Design"];

type Testimonial = (typeof testimonials)[0];

// Star Rating Component
function StarRating({ rating }: { rating: number }) {
	return (
		<div className="flex items-center gap-0.5">
			{[1, 2, 3, 4, 5].map((star) => (
				<svg
					key={star}
					className={`w-3.5 h-3.5 ${star <= rating ? "text-amber-400" : "text-gray-300"}`}
					fill={star <= rating ? "currentColor" : "none"}
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={2}
						d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
					/>
				</svg>
			))}
		</div>
	);
}

// Helper functions
function getInitials(name: string) {
	return name
		.split(" ")
		.map((n) => n[0])
		.join("")
		.toUpperCase();
}

function getGradient(name: string) {
	const gradients = [
		"from-violet-500 to-purple-600",
		"from-blue-500 to-cyan-500",
		"from-emerald-500 to-teal-500",
		"from-orange-500 to-amber-500",
		"from-pink-500 to-rose-500",
		"from-indigo-500 to-blue-500",
	];
	const index = name.charCodeAt(0) % gradients.length;
	return gradients[index];
}

// Light Theme Card
function TestimonialCard({ testimonial }: { testimonial: Testimonial }) {
	return (
		<div className="group relative bg-white rounded-2xl p-5 transition-all duration-300 hover:shadow-xl hover:shadow-indigo-500/10 border border-gray-100 hover:border-indigo-200">
			{/* Decorative corner accent */}
			<div className="absolute top-0 right-0 w-20 h-20 overflow-hidden rounded-tr-2xl">
				<div className="absolute -top-10 -right-10 w-20 h-20 bg-gradient-to-br from-indigo-100 to-purple-100 rotate-45 opacity-50 group-hover:opacity-80 transition-opacity" />
			</div>

			{/* Media content */}
			{testimonial.image && (
				<div className="mb-4 -mx-1 -mt-1 overflow-hidden rounded-xl shadow-sm">
					<img src={testimonial.image} alt="" className="w-full h-40 object-cover transition-transform duration-300 group-hover:scale-105" />
				</div>
			)}

			{testimonial.type === "video" && testimonial.videoThumbnail && (
				<div className="mb-4 -mx-1 -mt-1 overflow-hidden rounded-xl relative shadow-sm">
					<img
						src={testimonial.videoThumbnail}
						alt=""
						className="w-full h-40 object-cover transition-transform duration-300 group-hover:scale-105"
					/>
					<div className="absolute inset-0 flex items-center justify-center bg-black/20">
						<div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
							<svg className="w-5 h-5 text-indigo-600 ml-1" fill="currentColor" viewBox="0 0 24 24">
								<path d="M8 5v14l11-7z" />
							</svg>
						</div>
					</div>
				</div>
			)}

			{/* Rating */}
			<div className="mb-3">
				<StarRating rating={testimonial.rating} />
			</div>

			{/* Testimonial content */}
			<p className="text-gray-600 text-sm leading-relaxed mb-4">{testimonial.content}</p>

			{/* Author info */}
			<div className="flex items-center gap-3 pt-3 border-t border-gray-100">
				<div
					className={`w-10 h-10 rounded-full bg-gradient-to-br ${getGradient(testimonial.name)} flex items-center justify-center text-white text-xs font-semibold shadow-md`}
				>
					{getInitials(testimonial.name)}
				</div>
				<div className="flex-1 min-w-0">
					<p className="text-gray-900 font-medium text-sm truncate">{testimonial.name}</p>
					<p className="text-gray-500 text-xs truncate">
						{testimonial.role} at {testimonial.company}
					</p>
				</div>
			</div>
		</div>
	);
}

export default function WallOfLoveLightPublicPage() {
	const [activeFilter, setActiveFilter] = useState("All");

	const filteredTestimonials = activeFilter === "All" ? testimonials : testimonials.filter((t) => t.tags.includes(activeFilter));

	return (
		<div
			className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-indigo-50"
			style={{
				fontFamily: '"Google Sans", "Product Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
			}}
		>
			{/* Google Sans font */}
			<style>{`
				@import url('https://fonts.googleapis.com/css2?family=Google+Sans:wght@400;500;700&display=swap');

				@font-face {
					font-family: 'Google Sans';
					src: local('Google Sans'), local('GoogleSans-Regular');
					font-weight: 400;
				}
				@font-face {
					font-family: 'Google Sans';
					src: local('Google Sans Medium'), local('GoogleSans-Medium');
					font-weight: 500;
				}
				@font-face {
					font-family: 'Google Sans';
					src: local('Google Sans Bold'), local('GoogleSans-Bold');
					font-weight: 700;
				}
			`}</style>

			{/* Background effects */}
			<div className="fixed inset-0 overflow-hidden pointer-events-none">
				<div className="absolute top-0 left-1/4 w-96 h-96 bg-indigo-200/40 rounded-full blur-3xl" />
				<div className="absolute top-1/3 right-1/4 w-80 h-80 bg-purple-200/30 rounded-full blur-3xl" />
				<div className="absolute bottom-1/4 left-1/3 w-72 h-72 bg-pink-200/30 rounded-full blur-3xl" />
			</div>

			{/* Content */}
			<div className="relative z-10 max-w-7xl mx-auto px-6 py-16">
				{/* Header */}
				<div className="mb-12 flex items-center justify-between">
					{/* Left side - Title and description */}
					<div className="flex-1">
						<h1 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight text-gray-900">
							Wall of Love
						</h1>

						<p className="text-lg max-w-2xl leading-relaxed text-gray-600">
							Discover what our customers are saying. Real stories, real results, real impact on businesses around the world.
						</p>
					</div>

					{/* Right side - Animated heart icon */}
					<div className="hidden md:flex items-center justify-center flex-shrink-0 ml-8">
						<div className="relative">
							{/* Outer glow effect */}
							<div
								className="absolute inset-0 w-56 h-56 rounded-full blur-3xl animate-pulse bg-indigo-400/20"
								style={{ animationDuration: "3s" }}
							/>
							{/* Heart icon with fade animation */}
							<svg
								className="relative w-56 h-56 animate-pulse text-indigo-400/50"
								style={{ animationDuration: "2.5s" }}
								fill="currentColor"
								viewBox="0 0 24 24"
							>
								<path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
							</svg>
							{/* Inner smaller heart */}
							<svg
								className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-28 h-28 animate-pulse text-indigo-300/40"
								style={{ animationDuration: "2s", animationDelay: "0.5s" }}
								fill="currentColor"
								viewBox="0 0 24 24"
							>
								<path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
							</svg>
						</div>
					</div>
				</div>

				{/* Filter tags */}
				<div className="flex flex-wrap justify-start gap-2 mb-10">
					{filterTags.map((tag) => (
						<button
							key={tag}
							onClick={() => setActiveFilter(tag)}
							className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
								activeFilter === tag
									? "bg-indigo-500 text-white"
									: "bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 border border-gray-200"
							}`}
						>
							{tag}
						</button>
					))}
				</div>

				{/* Masonry grid */}
				<div className="columns-1 md:columns-2 lg:columns-3 gap-4 space-y-4">
					{filteredTestimonials.map((testimonial) => (
						<div key={testimonial.id} className="break-inside-avoid">
							<TestimonialCard testimonial={testimonial} />
						</div>
					))}
				</div>

				{/* Empty state */}
				{filteredTestimonials.length === 0 && (
					<div className="text-center py-20">
						<div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-gray-100">
							<svg
								className="w-8 h-8 text-gray-400"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
								/>
							</svg>
						</div>
						<p className="text-lg text-gray-600">No testimonials found for this filter.</p>
					</div>
				)}

				{/* Footer CTA */}
				<div className="mt-16 text-center">
					<div className="inline-flex flex-col items-center gap-4 p-8 rounded-2xl bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-100 shadow-lg">
						<h3 className="text-xl font-semibold text-gray-900">
							Want to share your experience?
						</h3>
						<p className="max-w-md text-gray-600">
							We'd love to hear from you! Share your story and help others discover what makes us special.
						</p>
						<button className="px-6 py-3 rounded-full font-medium shadow-lg transition-all duration-200 hover:scale-105 bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-indigo-500/25 hover:shadow-indigo-500/40">
							Share Your Story
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}

